export type KnowledgeLibraryBreadcrumbKind = 'root' | 'tag';

export interface KnowledgeLibraryBreadcrumb {
    kind: KnowledgeLibraryBreadcrumbKind;
    label: string;
    tagId?: string;
}

export interface KnowledgeLibraryTaxonomyCardModel {
    id: string;
    name: string;
    knowledgeBookCount: number;
    tag?: IClassificationTag;
}

export interface KnowledgeLibraryBookCardModel {
    id: string;
    name: string;
    description: string;
    knowledgeBook: IKnowledgeBook;
    classificationTags: IClassificationTag[];
}

export interface BuildKnowledgeLibraryBrowseModelInput {
    knowledgeBooks: IKnowledgeBook[];
    classificationTags: IClassificationTag[];
    selectedTagId?: string;
    allowedClassificationTagIds?: ReadonlySet<string> | null;
    classificationSortDirection?: 'asc' | 'desc';
    labels?: {
        library?: string;
        emptyRoot?: string;
        emptyTaxonomy?: string;
    };
}

export interface KnowledgeLibraryBrowseModel {
    breadcrumbs: KnowledgeLibraryBreadcrumb[];
    taxonomyCards: KnowledgeLibraryTaxonomyCardModel[];
    knowledgeBookCards: KnowledgeLibraryBookCardModel[];
    isRoot: boolean;
    emptyMessage: string;
}

const ROOT_PARENT_ID = '__knowledge_library_root__';

function compareByName<T extends { name: string }>(left: T, right: T): number {
    return left.name.localeCompare(right.name);
}

function getTagParentKey(tag: IClassificationTag): string {
    return tag.ParentID || ROOT_PARENT_ID;
}

function createKnowledgeBookCard(
    knowledgeBook: IKnowledgeBook,
    tagMap: Map<string, IClassificationTag>,
    allowedClassificationTagIds?: ReadonlySet<string> | null,
    classificationSortDirection: 'asc' | 'desc' = 'asc'
): KnowledgeLibraryBookCardModel {
    const classificationTags = (knowledgeBook.ClassificationTags || [])
        .filter(tagId => !allowedClassificationTagIds || allowedClassificationTagIds.has(tagId))
        .map(tagId => tagMap.get(tagId))
        .filter((tag): tag is IClassificationTag => !!tag)
        .sort((left, right) => classificationSortDirection === 'asc'
            ? left.Name.localeCompare(right.Name)
            : right.Name.localeCompare(left.Name));

    return {
        id: knowledgeBook.ID,
        name: knowledgeBook.Name,
        description: knowledgeBook.Description || '',
        knowledgeBook,
        classificationTags,
    };
}

export function filterKnowledgeBooksBySearchText(input: {
    knowledgeBooks: IKnowledgeBook[];
    classificationTags: IClassificationTag[];
    searchText: string;
}): IKnowledgeBook[] {
    const trimmedSearchText = input.searchText.trim().toLowerCase();
    if (!trimmedSearchText) {
        return input.knowledgeBooks;
    }

    const tagMap = new Map(input.classificationTags.map(tag => [tag.ID, tag]));
    const searchableTagTextCache = new Map<string, string>();

    const getSearchableTagText = (tagId: string): string => {
        const cachedValue = searchableTagTextCache.get(tagId);
        if (cachedValue) {
            return cachedValue;
        }

        const names: string[] = [];
        let currentTag = tagMap.get(tagId);
        while (currentTag) {
            names.push(currentTag.Name.toLowerCase());
            currentTag = currentTag.ParentID ? tagMap.get(currentTag.ParentID) : undefined;
        }

        const searchableText = names.join(' ');
        searchableTagTextCache.set(tagId, searchableText);
        return searchableText;
    };

    return input.knowledgeBooks.filter(knowledgeBook => {
        if (knowledgeBook.Name?.toLowerCase().includes(trimmedSearchText)
            || knowledgeBook.Description?.toLowerCase().includes(trimmedSearchText)) {
            return true;
        }

        return (knowledgeBook.ClassificationTags || []).some(tagId => getSearchableTagText(tagId).includes(trimmedSearchText));
    });
}

export function buildKnowledgeLibraryBrowseModel(input: BuildKnowledgeLibraryBrowseModelInput): KnowledgeLibraryBrowseModel {
    const labels = {
        library: 'Library',
        emptyRoot: 'No knowledge books match the current filters.',
        emptyTaxonomy: 'No knowledge books are assigned to this classification.',
        ...input.labels
    };

    const tagMap = new Map<string, IClassificationTag>();
    const childrenByParentId = new Map<string, IClassificationTag[]>();
    const directKnowledgeBooksByTagId = new Map<string, IKnowledgeBook[]>();
    const allowedClassificationTagIds = input.allowedClassificationTagIds || null;
    const classificationSortDirection = input.classificationSortDirection || 'asc';
    const compareClassificationNames = (left: { Name: string }, right: { Name: string }): number => {
        const comparison = left.Name.localeCompare(right.Name);
        return classificationSortDirection === 'asc' ? comparison : -comparison;
    };
    const compareCardNames = <T extends { name: string }>(left: T, right: T): number => {
        const comparison = left.name.localeCompare(right.name);
        return classificationSortDirection === 'asc' ? comparison : -comparison;
    };
    input.classificationTags.forEach(tag => {
        tagMap.set(tag.ID, tag);
        const parentKey = getTagParentKey(tag);
        const siblings = childrenByParentId.get(parentKey) || [];
        siblings.push(tag);
        childrenByParentId.set(parentKey, siblings);
    });

    childrenByParentId.forEach(children => children.sort(compareClassificationNames));

    const sortedKnowledgeBooks = [...input.knowledgeBooks].sort((left, right) => left.Name.localeCompare(right.Name));

    sortedKnowledgeBooks.forEach(knowledgeBook => {
        const classificationTagIds = knowledgeBook.ClassificationTags || [];

        if (classificationTagIds.length === 0) {
            return;
        }

        classificationTagIds.forEach(tagId => {
            if (allowedClassificationTagIds && !allowedClassificationTagIds.has(tagId)) {
                return;
            }

            if (!tagMap.has(tagId)) {
                return;
            }

            const directKnowledgeBooks = directKnowledgeBooksByTagId.get(tagId) || [];
            directKnowledgeBooks.push(knowledgeBook);
            directKnowledgeBooksByTagId.set(tagId, directKnowledgeBooks);
        });
    });

    const subtreeKnowledgeBookIdCache = new Map<string, Set<string>>();
    const getSubtreeKnowledgeBookIds = (tagId: string): Set<string> => {
        const cachedIds = subtreeKnowledgeBookIdCache.get(tagId);
        if (cachedIds) {
            return cachedIds;
        }

        const knowledgeBookIds = new Set<string>();
        (directKnowledgeBooksByTagId.get(tagId) || []).forEach(knowledgeBook => knowledgeBookIds.add(knowledgeBook.ID));
        (childrenByParentId.get(tagId) || []).forEach(childTag => {
            getSubtreeKnowledgeBookIds(childTag.ID).forEach(knowledgeBookId => knowledgeBookIds.add(knowledgeBookId));
        });

        subtreeKnowledgeBookIdCache.set(tagId, knowledgeBookIds);
        return knowledgeBookIds;
    };

    const createTaxonomyCard = (tag: IClassificationTag): KnowledgeLibraryTaxonomyCardModel => ({
        id: tag.ID,
        name: tag.Name,
        tag,
        knowledgeBookCount: getSubtreeKnowledgeBookIds(tag.ID).size
    });

    const createKnowledgeBookCards = (knowledgeBooks: IKnowledgeBook[]): KnowledgeLibraryBookCardModel[] => {
        return knowledgeBooks
            .map(knowledgeBook => createKnowledgeBookCard(knowledgeBook, tagMap, allowedClassificationTagIds, classificationSortDirection))
            .sort(compareCardNames);
    };

    const rootBreadcrumb: KnowledgeLibraryBreadcrumb = {
        kind: 'root',
        label: labels.library
    };

    const selectedTag = input.selectedTagId ? tagMap.get(input.selectedTagId) : undefined;
    if (selectedTag) {
        const ancestorTags: IClassificationTag[] = [];
        let currentTag: IClassificationTag | undefined = selectedTag;

        while (currentTag) {
            ancestorTags.unshift(currentTag);
            currentTag = currentTag.ParentID ? tagMap.get(currentTag.ParentID) : undefined;
        }

        const taxonomyCards = (childrenByParentId.get(selectedTag.ID) || [])
            .map(createTaxonomyCard)
            .filter(card => card.knowledgeBookCount > 0)
            .sort(compareCardNames);
        const knowledgeBookCards = createKnowledgeBookCards(directKnowledgeBooksByTagId.get(selectedTag.ID) || []);

        return {
            breadcrumbs: [
                rootBreadcrumb,
                ...ancestorTags.map<KnowledgeLibraryBreadcrumb>(tag => ({
                    kind: 'tag',
                    label: tag.Name,
                    tagId: tag.ID
                }))
            ],
            taxonomyCards,
            knowledgeBookCards,
            isRoot: false,
            emptyMessage: taxonomyCards.length === 0 && knowledgeBookCards.length === 0 ? labels.emptyTaxonomy : ''
        };
    }

    const taxonomyCards = (childrenByParentId.get(ROOT_PARENT_ID) || [])
        .map(createTaxonomyCard)
        .filter(card => card.knowledgeBookCount > 0)
        .sort(compareCardNames);

    return {
        breadcrumbs: [rootBreadcrumb],
        taxonomyCards,
        knowledgeBookCards: [],
        isRoot: true,
        emptyMessage: taxonomyCards.length === 0 ? labels.emptyRoot : ''
    };
}
